import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Star, SlidersHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { getProducts, getCategories } from '../services/api';

const Products = ({ onAddToCart }) => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const searchQuery = (searchParams.get('search') || '').trim().toLowerCase();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(
    categoryParam ? [categoryParam] : []
  );
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [sortBy, setSortBy] = useState('featured');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [productsData, categoriesData] = await Promise.all([
      getProducts(),
      getCategories()
    ]);
    setProducts(productsData);
    setCategories(categoriesData);
    setLoading(false);
  };

  useEffect(() => {
    if (products.length > 0) {
      let filtered = [...products];

      // Filter by search query (matches name, description, subcategory, category)
      if (searchQuery) {
        filtered = filtered.filter(p => {
          const haystack = [
            p.name,
            p.description,
            p.subcategory,
            p.category,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchQuery);
        });
      }

      // Filter by category
      if (selectedCategories.length > 0) {
        filtered = filtered.filter(p => selectedCategories.includes(p.category));
      }

      // Filter by subcategory
      if (selectedSubcategories.length > 0) {
        filtered = filtered.filter(p => selectedSubcategories.includes(p.subcategory));
      }

      // Sort
      switch (sortBy) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'rating':
          filtered.sort((a, b) => b.rating - a.rating);
          break;
        default:
          break;
      }

      setFilteredProducts(filtered);
    }
  }, [products, selectedCategories, selectedSubcategories, sortBy, searchQuery]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSubcategoryToggle = (subcategory) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategory)
        ? prev.filter(s => s !== subcategory)
        : [...prev, subcategory]
    );
  };

  const allSubcategories = [...new Set(products.map(p => p.subcategory))];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const FilterContent = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Categories</h3>
        <div className="space-y-3">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox
                id={category.id}
                checked={selectedCategories.includes(category.name)}
                onCheckedChange={() => handleCategoryToggle(category.name)}
              />
              <Label htmlFor={category.id} className="cursor-pointer text-slate-700">
                {category.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Subcategories */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Type</h3>
        <div className="space-y-3">
          {allSubcategories.map((subcategory) => (
            <div key={subcategory} className="flex items-center space-x-2">
              <Checkbox
                id={subcategory}
                checked={selectedSubcategories.includes(subcategory)}
                onCheckedChange={() => handleSubcategoryToggle(subcategory)}
              />
              <Label htmlFor={subcategory} className="cursor-pointer text-slate-700">
                {subcategory}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">
          {searchQuery
            ? `Results for "${searchParams.get('search')}"`
            : (categoryParam || 'All Products')}
        </h1>
        <p className="text-slate-600">
          Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters and Sort Bar */}
      <div className="flex items-center justify-between mb-8">
        {/* Mobile Filter */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="md:hidden">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="left">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <FilterContent />
            </div>
          </SheetContent>
        </Sheet>

        {/* Sort */}
        <div className="flex items-center space-x-2 ml-auto">
          <span className="text-sm text-slate-600">Sort by:</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-4 gap-8">
        {/* Desktop Filters */}
        <div className="hidden md:block">
          <div className="sticky top-24">
            <FilterContent />
          </div>
        </div>

        {/* Products Grid */}
        <div className="md:col-span-3">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-600 mb-4">No products found matching your filters.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedSubcategories([]);
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="group hover:shadow-xl transition-all border-none overflow-hidden">
                  <Link to={`/product/${product.id}`}>
                    <div className="relative overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {product.badge && (
                        <span className="absolute top-4 right-4 bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-medium">
                          {product.badge}
                        </span>
                      )}
                    </div>
                  </Link>
                  <CardContent className="p-6">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-semibold text-slate-900 mb-2 hover:text-slate-700">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-slate-600 mb-3">{product.subcategory}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-light text-neutral-900">£{product.price}</span>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-neutral-600">{product.rating}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => onAddToCart(product)}
                      className="w-full bg-neutral-900 hover:bg-neutral-800 text-white"
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
